import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, LayoutDashboard, Boxes, LogOut, Users, FileBarChart } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inventory", label: "Flat Inventory", icon: Boxes, testid: "nav-inventory" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/team", label: "Team & Staff", icon: Users, testid: "nav-team", adminOnly: true },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 lg:w-72 border-r border-slate-200 bg-white flex-shrink-0 hidden md:flex flex-col">
        <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-200">
          <div className="h-9 w-9 rounded-lg bg-[#0F2942] flex items-center justify-center text-white">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <p className="font-display font-bold text-slate-900">EstatePulse</p>
            <p className="text-[10px] uppercase font-mono tracking-[0.18em] text-slate-400">Flat Registry</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.filter((i) => !i.adminOnly || user?.role === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0F2942] text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#0F2942] text-white text-xs">
                {user?.name?.slice(0, 2)?.toUpperCase() || "AD"}
              </AvatarFallback>
            </Avatar>
            <div className="leading-tight min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="current-user-name">{user?.name || "Admin"}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 text-slate-600"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 px-4 sm:px-6 bg-white/90 backdrop-blur-md sticky top-0 z-40 flex items-center justify-between">
          <div className="md:hidden flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#0F2942] flex items-center justify-center text-white">
              <Building2 className="h-4 w-4" />
            </div>
            <span className="font-display font-bold text-slate-900">EstatePulse</span>
          </div>
          <div className="hidden md:flex gap-2">
            {navItems.filter((i) => !i.adminOnly || user?.role === "admin").map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-sm font-medium px-3 py-1.5 rounded-md ${isActive ? "text-[#0F2942]" : "text-slate-400 hover:text-slate-700"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="flex md:hidden items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>Dash</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/inventory")}>Flats</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/reports")}>Reports</Button>
            {user?.role === "admin" && <Button variant="ghost" size="sm" onClick={() => navigate("/team")}>Team</Button>}
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button-mobile"><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
